import { useEffect, useState } from "react";
import axios from "axios";
import useReveal, { useCountUp } from "../../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  heading: "Our achievements",
  items: [
    { metric: "200+", label: "Products globally" },
    { metric: "25", label: "Years of expertise" },
    { metric: "500+", label: "Healthcare partners" },
    { metric: "12", label: "International markets" },
  ],
};

export default function Achievements() {
  const [data, setData] = useState(DEFAULTS);
  const { ref, visible, revealKey } = useReveal();

  useEffect(() => {
    axios.get(`${API}/public/pages/home.achievements`).then((r) => {
      if (r.data?.value && r.data.value.items?.length) setData({ ...DEFAULTS, ...r.data.value });
    }).catch(() => {});
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{ background: "linear-gradient(120deg, #C2410C 0%, #9A3412 50%, #0369A1 100%)" }}
      data-testid="home-achievements"
    >
      <div className="absolute inset-0 opacity-[0.07]" aria-hidden style={{
        backgroundImage: "radial-gradient(circle at 25% 25%, white 1px, transparent 1.5px), radial-gradient(circle at 75% 75%, white 1px, transparent 1.5px)",
        backgroundSize: "44px 44px",
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
        <div className={`max-w-2xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-xs tracking-[0.2em] uppercase font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-white" /> By the numbers
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {data.heading}
          </h2>
          <p className="mt-4 text-white/80 text-base sm:text-lg max-w-xl">
            Two decades of consistent science, scale and trust — distilled into the numbers below.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {data.items.map((it, i) => (
            <StatTile key={i} item={it} trigger={revealKey} visible={visible} delay={i * 120} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatTile({ item, trigger, visible, delay }) {
  const animated = useCountUp(item.metric, trigger);
  return (
    <div
      className="relative pl-5 border-l-2 border-white/30 transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transitionDelay: `${delay}ms` }}
      data-testid="achievement-tile"
    >
      <div className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {animated}
      </div>
      <div className="mt-2 text-sm sm:text-base text-white/80 leading-snug">{item.label}</div>
    </div>
  );
}
